/**
 * InfoTooltip — small reusable info icon with a hover/focus tooltip bubble.
 *
 * Used by Dashboard KPI cards (T1) to surface the definition of each metric,
 * and by the Launch Calendar cells (T4) to clarify the displayed values.
 *
 * Pure CSS tooltip (see `.info-tooltip*` rules in styles.css). The icon is
 * focusable so keyboard users can read the description; the description is
 * also exposed via aria-label for screen readers.
 */

import { escapeHtml } from "../utils/escapeHtml";

/**
 * Append an info tooltip icon to `host`. No-op if `host` is null or already
 * has an info tooltip mounted (idempotent — safe to call after re-renders).
 */
export function mountInfoTooltip(host: Element | null | undefined, text: string): void {
  if (!host) return;
  if (host.querySelector(":scope > .info-tooltip")) return;

  const wrapper = document.createElement("span");
  wrapper.className = "info-tooltip";
  wrapper.setAttribute("tabindex", "0");
  wrapper.setAttribute("role", "img");
  wrapper.setAttribute("aria-label", text);
  wrapper.innerHTML =
    `<span class="info-tooltip-icon" aria-hidden="true">i</span>` +
    `<span class="info-tooltip-bubble" role="tooltip">${escapeHtml(text)}</span>`;

  host.appendChild(wrapper);
}
